/*
  Aaratrika concept motion (Claude build). Plain JavaScript, no libraries.

  - [data-aa-reveal] elements are revealed once as they enter the viewport.
  - [data-aa-words] headlines are split into words that rise.
  - <aa-lunar-turn> is the pack carousel: five Lunar families in their locked
    order, turned by page scroll, drag, the arrow buttons or the keyboard.

  The page scrolls natively throughout; nothing here captures or blocks scroll.
  All content is in the HTML, so the page reads fully without this file.
*/

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

function splitWords(element) {
  if (element.dataset.aaWords === 'done') return;
  const words = element.textContent.trim().split(/\s+/);
  element.textContent = '';
  words.forEach((word, index) => {
    const mask = document.createElement('span');
    mask.className = 'aa-w';
    const inner = document.createElement('span');
    inner.textContent = word;
    inner.style.setProperty('--aa-i', String(index));
    mask.append(inner);
    element.append(mask, ' ');
  });
  element.dataset.aaWords = 'done';
}

const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-in');
      revealObserver.unobserve(entry.target);
    }
  },
  { threshold: 0.16, rootMargin: '0px 0px -8% 0px' }
);

/**
 * Gets a scene's pictures ready before it is reached: fetched and decoded while it is still
 * two screens away, so none of that work lands in a frame while the scene is on screen
 * (measured: five large moons decoding at once held one frame for a third of a second).
 * On wide screens the page scrolls inside .page-wrapper, and "two screens away" has to be
 * measured against that box, not the window.
 */
let warmObserver;
function warm(element) {
  if (!warmObserver) {
    const wrapper = document.querySelector('.page-wrapper');
    const scrolls = wrapper && /auto|scroll/.test(getComputedStyle(wrapper).overflowY) && wrapper.scrollHeight > wrapper.clientHeight + 1;
    warmObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        warmObserver.unobserve(entry.target);
        entry.target.querySelectorAll('img').forEach((image) => {
          image.loading = 'eager';
          const decode = () => image.decode?.().catch(() => {});
          if (image.complete) decode();
          else image.addEventListener('load', decode, { once: true });
        });
      }
    }, { root: scrolls ? wrapper : null, rootMargin: '200% 0px' });
  }
  warmObserver.observe(element);
}

function initReveals(root) {
  root.querySelectorAll('[data-aa-words]').forEach((element) => {
    splitWords(element);
    revealObserver.observe(element);
  });
  root.querySelectorAll('[data-aa-reveal]').forEach((element) => revealObserver.observe(element));
}

class AaLunarTurn extends HTMLElement {
  connectedCallback() {
    // One turn, shown twice: Trail on the left and Ember on the right. Each half has its own
    // five packs; both follow the same position, so the same family is in front on both.
    this.stage = this.querySelector('[data-stage]');
    this.groups = Array.from(this.querySelectorAll('.aa-lunar__stage')).map((stage) => ({
      stage,
      items: Array.from(stage.querySelectorAll('[data-family]')),
      spacing: 0,
      depth: 0,
    }));
    this.items = this.groups[0]?.items || [];
    this.familyButtons = Array.from(this.querySelectorAll('[data-go]'));
    this.caption = this.querySelector('[data-caption]');
    this.nameOutputs = Array.from(this.querySelectorAll('[data-caption-name]'));
    this.archetypeOutput = this.querySelector('[data-caption-line]');
    this.phraseOutput = this.querySelector('[data-caption-phrase]');
    this.previousButton = this.querySelector('[data-previous]');
    this.nextButton = this.querySelector('[data-next]');
    if (!this.stage || this.items.length === 0) return;

    this.last = this.items.length - 1;
    this.accents = this.items.map((item) => item.dataset.accent.split(' ').map(Number));
    this.position = 0;
    this.target = 0;
    this.active = -1;
    this.userControlled = false;
    this.visible = false;
    this.frame = 0;

    this.measure();
    this.render();

    this.abort = new AbortController();
    const { signal } = this.abort;
    window.addEventListener('resize', () => { this.measure(); this.render(); }, { signal });
    // Scroll events do not bubble; capturing on the document hears whichever element scrolls.
    document.addEventListener('scroll', () => this.onScroll(), { capture: true, passive: true, signal });
    this.visibility = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible) this.onScroll();
    });
    this.visibility.observe(this.stage);
    // Only the pack in front is in view; the others would load as they slide in, and arrive blank.
    warm(this);

    this.stage.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
    this.stage.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal });
    this.stage.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
    this.stage.addEventListener('pointercancel', (event) => this.onPointerUp(event), { signal });
    this.stage.addEventListener('keydown', (event) => this.onKeyDown(event), { signal });

    this.familyButtons.forEach((button, index) =>
      button.addEventListener('click', () => this.select(index), { signal })
    );
    this.previousButton?.addEventListener('click', () => this.select(this.active - 1), { signal });
    this.nextButton?.addEventListener('click', () => this.select(this.active + 1), { signal });
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.visibility?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  measure() {
    for (const group of this.groups) {
      const width = group.stage.clientWidth;
      // Far enough apart that only the pack in front is in its half; the others wait out of sight.
      group.spacing = width * 0.58;
      group.depth = Math.min(group.spacing, 320) * 0.7;
    }
    this.spacing = this.groups[0].spacing;
  }

  /** A choice made by the visitor: from here on, page scroll no longer turns the stage. */
  select(index) {
    this.userControlled = true;
    this.caption?.setAttribute('aria-live', 'polite');
    this.target = clamp(index, 0, this.last);
    this.run();
  }

  onScroll() {
    if (this.userControlled || !this.visible || reducedMotion.matches || this.dragging) return;
    const rect = this.stage.getBoundingClientRect();
    const centre = (rect.top + rect.height / 2) / window.innerHeight;
    // The cycle runs while the stage centre travels from 78% to 30% of the viewport height.
    this.target = clamp((0.78 - centre) / 0.48, 0, 1) * this.last;
    this.run();
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => {
      this.target = Math.round(this.target);
      this.run();
    }, 160);
  }

  onPointerDown(event) {
    if (event.button !== 0 || event.target.closest('button, a')) return;
    this.dragging = true;
    this.dragMoved = false;
    this.dragStartX = event.clientX;
    this.dragStartPosition = this.position;
  }

  onPointerMove(event) {
    if (!this.dragging) return;
    const delta = event.clientX - this.dragStartX;
    if (!this.dragMoved) {
      if (Math.abs(delta) < 6) return;
      this.dragMoved = true;
      this.stage.setPointerCapture(event.pointerId);
      this.stage.classList.add('is-dragging');
    }
    this.position = clamp(this.dragStartPosition - delta / this.spacing, -0.3, this.last + 0.3);
    this.target = this.position;
    this.render();
  }

  onPointerUp(event) {
    if (!this.dragging) return;
    this.dragging = false;
    this.stage.classList.remove('is-dragging');
    if (this.dragMoved) {
      // Carry on a little in the direction of the drag, then settle on a pack.
      const travelled = this.position - this.dragStartPosition;
      const carry = Math.abs(travelled) > 0.12 ? Math.sign(travelled) * 0.3 : 0;
      this.select(Math.round(this.position + carry));
      return;
    }
    const item = event.target.closest('[data-family]');
    const group = item && this.groups.find((candidate) => candidate.items.includes(item));
    if (group) this.select(group.items.indexOf(item));
  }

  onKeyDown(event) {
    const moves = { ArrowLeft: this.active - 1, ArrowRight: this.active + 1, Home: 0, End: this.last };
    if (!(event.key in moves)) return;
    event.preventDefault();
    this.select(moves[event.key]);
  }

  run() {
    if (this.frame) return;
    let last = 0;
    const step = (now) => {
      const elapsed = last ? Math.min(now - last, 64) : 16;
      last = now;
      const distance = this.target - this.position;
      if (reducedMotion.matches || Math.abs(distance) < 0.001) {
        this.position = this.target;
        this.frame = 0;
      } else {
        // Eased by the clock, so the turn has the same pace on any screen.
        this.position += distance * (1 - Math.exp(-elapsed / 140));
        this.frame = requestAnimationFrame(step);
      }
      this.render();
    };
    this.frame = requestAnimationFrame(step);
  }

  render() {
    for (const group of this.groups) {
      group.items.forEach((item, index) => {
        const offset = index - this.position;
        const distance = Math.abs(offset);
        const x = offset * group.spacing;
        const z = -Math.min(distance, 2.5) * group.depth;
        const turn = -clamp(offset, -1.4, 1.4) * 32;
        const scale = 1 - Math.min(distance, 2) * 0.08;
        item.style.transform = `translate3d(${x.toFixed(1)}px, 0, ${z.toFixed(1)}px) rotateY(${turn.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
        item.style.setProperty('--aa-dim', Math.min(0.74, distance * 0.36).toFixed(3));
        // One at a time: a pack fades as it slides away, and is gone before it reaches the edge.
        item.style.opacity = clamp(1 - (distance - 0.1) / 0.62, 0, 1).toFixed(3);
      });
    }

    const lower = clamp(Math.floor(this.position), 0, this.last);
    const upper = clamp(lower + 1, 0, this.last);
    const mix = clamp(this.position - lower, 0, 1);
    const tint = this.accents[lower].map((channel, i) => Math.round(channel + (this.accents[upper][i] - channel) * mix));
    this.style.setProperty('--aa-tint', tint.join(' '));

    const active = clamp(Math.round(this.position), 0, this.last);
    if (active !== this.active) {
      this.active = active;
      for (const group of this.groups) group.items.forEach((item, index) => item.classList.toggle('is-active', index === active));
      this.familyButtons.forEach((button, index) => {
        if (index === active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      if (this.previousButton) this.previousButton.disabled = active === 0;
      if (this.nextButton) this.nextButton.disabled = active === this.last;
      this.updateCaption();
    }
  }

  updateCaption() {
    const item = this.items[this.active];
    if (!item) return;
    this.nameOutputs.forEach((output) => { output.textContent = `${item.dataset.name} ${output.dataset.captionName}`; });
    if (this.archetypeOutput) this.archetypeOutput.textContent = item.dataset.archetype || '';
    if (this.phraseOutput) this.phraseOutput.textContent = item.dataset.phrase ? `“${item.dataset.phrase}”` : '';
  }
}

/**
 * Shopify returns from a contact or customer form to the same page as ?contact_posted=true
 * (or ?customer_posted=true) #<form id>, and every form of that kind on the page then reports
 * success. When the address names a form, only
 * that form keeps its confirmation (and puts its fields away); the others keep their fields.
 */
function settlePostedForms() {
  const named = window.location.hash.slice(1);
  if (!named) return;
  document.querySelectorAll('[data-success-for]').forEach((notice) => {
    if (notice.dataset.successFor === named) {
      notice.closest('form')?.querySelector('[data-fields]')?.setAttribute('hidden', '');
    } else {
      notice.setAttribute('hidden', '');
    }
  });
}

/**
 * "Join the waitlist": a button and the dialog it opens. The dialog's form is a plain
 * Shopify customer form (the email becomes a tagged customer), sent by the browser itself;
 * Shopify's spam check takes it over on the store's own address. This script only opens and
 * closes the dialog, and reopens it when Shopify brings the visitor back with a confirmation
 * or an error.
 */
class AaWaitlist extends HTMLElement {
  connectedCallback() {
    const dialog = this.querySelector('dialog');
    const backdrop = this.querySelector('[data-backdrop]');
    if (!dialog || !backdrop) return;
    // Not modal: a modal dialog makes the rest of the page inert, and Shopify's spam check,
    // which works outside the dialog, could then never finish the submission (Avinash,
    // 6 October 2026, on his phone: "the page is stuck"). The dialog and its backdrop are moved
    // to the end of the body, fixed to the screen, clear of any animated ancestor.
    dialog.classList.add('aa-scope');
    document.body.append(backdrop, dialog);
    this.portaled = [backdrop, dialog];
    const onKey = (event) => { if (event.key === 'Escape') close(); };
    const open = () => {
      backdrop.hidden = false;
      if (!dialog.open) dialog.show();
      document.addEventListener('keydown', onKey);
      dialog.querySelector('input:not([type="hidden"]), button[type="submit"], [data-close]')?.focus();
    };
    const close = () => {
      dialog.close();
      backdrop.hidden = true;
      document.removeEventListener('keydown', onKey);
    };
    this.querySelector('[data-open]')?.addEventListener('click', open);
    dialog.querySelector('[data-close]')?.addEventListener('click', close);
    backdrop.addEventListener('click', close);

    // Reopen when Shopify brings the visitor back with a confirmation or an error, or when a link points here.
    const notice = dialog.querySelector('[data-success]');
    const error = dialog.querySelector('[data-error]');
    if ((notice && !notice.hidden) || error || window.location.hash === '#waitlist') open();
  }

  disconnectedCallback() {
    this.portaled?.forEach((node) => node.remove());
  }
}

/**
 * Reports how far the page has scrolled through a tall section, as --aa-p from 0 to 1.
 * The section's own CSS turns that number into the picture (the Diwali box opening).
 * It only measures; it never moves or holds the page.
 */
class AaScrollStage extends HTMLElement {
  connectedCallback() {
    if (reducedMotion.matches) return;
    warm(this);
    this.abort = new AbortController();
    let queued = false;
    const eye = this.querySelector('[data-aa-eye]');
    const measure = () => {
      queued = false;
      const rect = this.getBoundingClientRect();
      const view = window.innerHeight;
      const travel = rect.height - view;
      let progress;
      if (travel > view * 0.4 && !eye) {
        // A tall track: how far it has scrolled past.
        progress = clamp(-rect.top / travel, 0, 1);
      } else if (eye && travel > view * 0.4 && getComputedStyle(eye.closest('.aa-diwali__sticky') || eye).position === 'sticky') {
        // The stage held in view on a large screen: the same, the track scrolling past.
        progress = clamp(-rect.top / travel, 0, 1);
      } else if (eye) {
        // A section of ordinary height (phones): how far the stage's eye, the picture window, has
        // crossed the screen, so the scenes change while the window can be seen (Avinash,
        // 6 October 2026: on his phone the hand-over came only once the text was in view).
        const e = eye.getBoundingClientRect();
        progress = clamp((view - e.top) / (view * 0.8 + e.height), 0, 1);
      } else {
        progress = clamp((view * 0.8 - rect.top) / (view * 0.9), 0, 1);
      }
      this.style.setProperty('--aa-p', progress.toFixed(3));
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(measure);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true, signal: this.abort.signal });
    window.addEventListener('resize', onScroll, { signal: this.abort.signal });
    measure();
  }

  disconnectedCallback() {
    this.abort?.abort();
  }
}

const smooth = (t) => t * t * (3 - 2 * t);
const mixRgb = (a, b, t) => a.map((channel, i) => Math.round(channel + (b[i] - channel) * t));
const rgbOf = (text) => String(text || '').trim().split(/\s+/).map(Number);

/**
 * Follows a number that scrolling sets, but glides to it. Whatever it paints therefore
 * never jumps, however the page was scrolled (a wheel moves a page in steps; this does not
 * touch the page, only how the picture catches up with it).
 */
class Glide {
  constructor(paint, ease = 170) {
    this.paint = paint;
    this.ease = ease;
    this.value = 0;
    this.target = 0;
    this.frame = 0;
    this.last = 0;
    this.step = this.step.bind(this);
  }

  to(target, instantly = false) {
    this.target = target;
    if (instantly) { this.value = target; this.paint(target); return; }
    if (!this.frame) this.frame = requestAnimationFrame(this.step);
  }

  step(now) {
    const elapsed = this.last ? Math.min(now - this.last, 64) : 16;
    this.last = now;
    this.value += (this.target - this.value) * (1 - Math.exp(-elapsed / this.ease));
    if (Math.abs(this.target - this.value) < 0.0004) {
      this.value = this.target;
      this.frame = 0;
      this.last = 0;
    } else {
      this.frame = requestAnimationFrame(this.step);
    }
    this.paint(this.value);
  }

  stop() {
    cancelAnimationFrame(this.frame);
    this.frame = 0;
  }
}

/** Calls back (at most once a frame) whenever the page scrolls or the window changes size. */
function onPageScroll(callback, signal) {
  let queued = false;
  const run = () => { queued = false; callback(); };
  const queue = () => { if (!queued) { queued = true; requestAnimationFrame(run); } };
  // Scroll events do not bubble; capturing on the document hears whichever element scrolls.
  document.addEventListener('scroll', queue, { capture: true, passive: true, signal });
  window.addEventListener('resize', queue, { signal });
}

/** How far an element has travelled up the screen: 0 as its middle passes `from` (a share of the
    screen's height, measured from the top), 1 as it passes `to`. */
function passProgress(element, from = 0.8, to = 0.32) {
  const rect = element.getBoundingClientRect();
  const middle = (rect.top + rect.height / 2) / window.innerHeight;
  return clamp((from - middle) / (from - to), 0, 1);
}

/**
 * The Botanical stage. As the page reaches it the four packs open like a hand of cards
 * into a row; scrolling on brings each forward in turn, and the panel's colour flows from
 * one pack's to the next. Pointing at a pack (or reaching it with the keyboard) brings
 * that one forward. Without the script, or with reduced motion, it is a plain row.
 */
class AaGarden extends HTMLElement {
  connectedCallback() {
    this.packs = Array.from(this.querySelectorAll('.aa-garden__pack'));
    this.nameOutput = this.querySelector('[data-garden-name]');
    this.flora = this.querySelector('.aa-garden__flora');
    this.fore = this.querySelector('.aa-garden__fore');
    this.glow = this.querySelector('.aa-garden__glow');
    if (this.packs.length === 0) return;
    this.washes = this.packs.map((pack) => rgbOf(pack.dataset.wash));
    this.named = -1;
    this.pointed = -1;
    this.held = 0;
    // A pack under the pointer (or the keyboard) comes forward; `held` eases between the
    // one the scroll has reached and the one pointed at, so nothing jumps.
    this.hold = new Glide((value) => { this.held = value; this.paint(this.glide ? this.glide.value : 0); }, 150);
    const point = (index) => { this.pointed = index; this.hold.to(1, reducedMotion.matches); };
    const release = () => this.hold.to(0, reducedMotion.matches);
    this.packs.forEach((pack, index) => {
      pack.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') point(index); });
      pack.addEventListener('pointerleave', release);
      pack.addEventListener('focus', () => point(index));
      pack.addEventListener('blur', release);
    });
    if (reducedMotion.matches) return;
    warm(this);
    this.classList.add('is-driven');
    this.glide = new Glide((value) => this.paint(value), 200);
    this.abort = new AbortController();
    this.track = this.closest('.aa-gardentrack');
    const measure = () => {
      // Where the panel holds still: in the middle of the screen, clear of the header.
      this.pinTop = Math.max(88, (window.innerHeight - this.offsetHeight) / 2 + 24);
      this.style.setProperty('--aa-pin-top', `${Math.round(this.pinTop)}px`);
      this.travel = this.track ? this.track.offsetHeight - this.offsetHeight : 0;
    };
    const read = () => {
      if (!this.visible) return;
      if (this.travel > 120) {
        // Rising into place opens the fan; the scroll while it is held walks the row.
        const view = window.innerHeight;
        const top = this.track.getBoundingClientRect().top;
        const entry = clamp((view * 0.95 - top) / (view * 0.95 - this.pinTop), 0, 1);
        const held = clamp((this.pinTop - top) / this.travel, 0, 1);
        this.glide.to(entry * 0.32 + held * 0.68);
      } else {
        this.glide.to(passProgress(this, 0.92, 0.42));
      }
    };
    onPageScroll(read, this.abort.signal);
    window.addEventListener('resize', () => { this.stepWidth = 0; this.centres = null; measure(); }, { signal: this.abort.signal });
    this.visibility = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; if (this.visible) { measure(); read(); } }, { rootMargin: '20% 0px' });
    this.visibility.observe(this);
    this.paint(0);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.visibility?.disconnect();
    this.glide?.stop();
    this.hold?.stop();
  }

  paint(progress) {
    const last = this.packs.length - 1;
    const middle = last / 2;
    // The first part of the way opens the fan; the rest walks along the row.
    const open = this.glide ? smooth(clamp(progress / 0.34, 0, 1)) : 1;
    const walk = this.glide ? clamp((progress - 0.3) / 0.62, 0, 1) * last : 0;
    const front = this.pointed >= 0 ? walk + (this.pointed - walk) * this.held : walk;
    if (!this.stepWidth) this.stepWidth = this.packs[0].offsetWidth + (parseFloat(getComputedStyle(this.packs[0].parentElement.parentElement).columnGap) || 0);
    const step = this.stepWidth;
    this.packs.forEach((pack, index) => {
      const near = smooth(clamp(1 - Math.abs(front - index), 0, 1)) * open;
      const closed = 1 - open;
      const x = (middle - index) * step * 0.86 * closed;
      const turn = (index - middle) * 9 * closed;
      const y = closed * 34 - near * 20;
      pack.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) rotate(${turn.toFixed(2)}deg) scale(${(1 + near * 0.06).toFixed(3)})`;
      pack.style.opacity = clamp(0.25 + open * 0.75, 0, 1).toFixed(3);
      pack.style.filter = `saturate(${(0.78 + near * 0.22).toFixed(3)}) brightness(${(0.95 + near * 0.05).toFixed(3)})`;
      pack.style.zIndex = String(Math.round(near * 10) + (closed > 0.02 ? index : 0));
      pack.classList.toggle('is-active', near > 0.6);
    });
    const lower = clamp(Math.floor(front), 0, last);
    const upper = clamp(lower + 1, 0, last);
    this.style.setProperty('--aa-wash', mixRgb(this.washes[lower], this.washes[upper], smooth(clamp(front - lower, 0, 1))).join(' '));
    const named = clamp(Math.round(front), 0, last);
    if (named !== this.named) {
      this.named = named;
      if (this.nameOutput) this.nameOutput.textContent = this.packs[named].dataset.name;
    }
    if (!this.glide) return;
    // The name fades out as one pack hands over to the next, and in again as the next arrives.
    if (this.nameOutput) this.nameOutput.style.opacity = smooth(clamp(1.08 - Math.abs(front - named) * 2.16, 0, 1)).toFixed(3);
    // The garden rises into place as the panel opens, then drifts a little against the page;
    // the blossoms nearest the lens drift further, the other way.
    if (this.flora) {
      this.flora.style.transform = `translate3d(${((0.5 - progress) * 2).toFixed(3)}%, ${((1 - open) * 11 + (0.5 - progress) * 3).toFixed(3)}%, 0) scale(1.07)`;
      this.flora.style.opacity = (0.15 + open * 0.85).toFixed(3);
    }
    if (this.fore) this.fore.style.transform = `translate3d(${((progress - 0.5) * 6).toFixed(3)}%, ${((1 - open) * 70).toFixed(2)}%, 0)`;
    // A soft light stands behind the pack in front and travels along the row with it.
    if (this.glow) {
      if (!this.centres) {
        // Measured by layout, not by what is on screen, so a panel still easing in reads true.
        this.centres = this.packs.map((pack) => {
          const item = pack.parentElement;
          const row = item.offsetParent;
          return [row.offsetLeft + item.offsetLeft + item.offsetWidth / 2, row.offsetTop + item.offsetTop + item.offsetHeight / 2];
        });
      }
      const mix = smooth(clamp(front - lower, 0, 1));
      const x = this.centres[lower][0] + (this.centres[upper][0] - this.centres[lower][0]) * mix;
      const y = this.centres[lower][1] + (this.centres[upper][1] - this.centres[lower][1]) * mix;
      this.glow.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
      this.glow.style.opacity = open.toFixed(3);
    }
  }
}

/**
 * A row that the scroll walks through. As the page passes it each item comes forward in
 * turn, smoothly: its neighbours ease back, the host takes its tone (--aa-tone, flowing
 * from one item's to the next), and its name and line are written beneath. At either end
 * of the pass the row is whole and nothing is singled out. On a phone, where the row is
 * swiped sideways, the row also glides sideways to keep the one in front in view.
 */
class AaScrollSteps extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-step]'));
    this.nameOutput = this.querySelector('[data-step-name]');
    this.lineOutput = this.querySelector('[data-step-line]');
    this.caption = this.nameOutput?.parentElement;
    this.row = this.querySelector('[data-step-row]');
    if (this.items.length === 0 || reducedMotion.matches) return;
    this.paper = rgbOf(this.dataset.paper || '245 240 232');
    this.tones = this.items.map((item) => (item.dataset.tone ? rgbOf(item.dataset.tone) : this.paper));
    this.named = -2;
    warm(this);
    this.classList.add('is-driven');
    this.glide = new Glide((value) => this.paint(value), 190);
    this.abort = new AbortController();
    const read = () => { if (this.visible) this.glide.to(passProgress(this.row || this, 0.78, 0.24)); };
    onPageScroll(read, this.abort.signal);
    this.visibility = new IntersectionObserver(([entry]) => { this.visible = entry.isIntersecting; if (this.visible) read(); }, { rootMargin: '20% 0px' });
    this.visibility.observe(this);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.visibility?.disconnect();
    this.glide?.stop();
  }

  paint(progress) {
    const last = this.items.length - 1;
    const edge = 0.1;
    // 0 at either end of the pass (the row whole), 1 through the middle (one in front).
    const presence = smooth(clamp(progress / edge, 0, 1)) * smooth(clamp((1 - progress) / edge, 0, 1));
    const front = clamp((progress - edge) / (1 - 2 * edge), 0, 1) * last;
    this.items.forEach((item, index) => {
      const near = smooth(clamp(1 - Math.abs(front - index), 0, 1));
      const back = presence * (1 - near);
      item.style.opacity = (1 - back * 0.52).toFixed(3);
      item.style.transform = `translate3d(0, ${(-14 * near * presence).toFixed(2)}px, 0)`;
      item.style.filter = back > 0.01 ? `saturate(${(1 - back * 0.45).toFixed(3)})` : 'none';
    });
    const lower = clamp(Math.floor(front), 0, last);
    const upper = clamp(lower + 1, 0, last);
    const tone = mixRgb(this.paper, mixRgb(this.tones[lower], this.tones[upper], smooth(clamp(front - lower, 0, 1))), presence);
    this.style.setProperty('--aa-tone', tone.join(' '));

    const named = presence > 0.5 ? clamp(Math.round(front), 0, last) : -1;
    if (named !== this.named) {
      this.named = named;
      const item = this.items[named];
      if (this.nameOutput) this.nameOutput.textContent = item?.dataset.name || '';
      if (this.lineOutput) this.lineOutput.textContent = item?.dataset.line || '';
    }
    if (this.caption) this.caption.style.opacity = presence.toFixed(3);

    // Swiped sideways on a phone: glide the row so the one in front stays in view.
    if (this.row && this.row.scrollWidth > this.row.clientWidth + 4 && presence > 0.02) {
      const a = this.items[lower];
      const b = this.items[upper];
      const centre = (item) => item.offsetLeft - (this.row.clientWidth - item.clientWidth) / 2;
      this.row.scrollLeft = centre(a) + (centre(b) - centre(a)) * clamp(front - lower, 0, 1);
    }
  }
}

/**
 * The two doors as one scene. A tall track scrolls past while the doors stay in view; how
 * far through the track the page is decides which Lunar family, and which Woven collection
 * beside it, is showing. Each gives way to the next by dissolving, and the whole thing
 * glides to where the scroll is rather than snapping to it.
 */
class AaDoorStage extends HTMLElement {
  connectedCallback() {
    if (reducedMotion.matches || window.innerHeight < 560) return;
    this.groups = ['.aa-moon__face', '.aa-moon__glow', '.aa-cloth', '.aa-door__tones span']
      .map((selector) => Array.from(this.querySelectorAll(selector)))
      .filter((group) => group.length > 1);
    this.names = Array.from(this.querySelectorAll('.aa-door__names')).map((list) => Array.from(list.children));
    this.moon = this.querySelector('.aa-moon');
    this.count = Math.max(2, ...this.groups.map((group) => group.length));
    if (this.groups.length === 0) return;
    warm(this);
    this.classList.add('is-driven');
    this.glide = new Glide((value) => this.paint(value), 210);
    this.abort = new AbortController();
    const read = () => {
      const rect = this.getBoundingClientRect();
      const travel = rect.height - window.innerHeight;
      if (travel <= 0) return;
      this.glide.to(clamp(-rect.top / travel, 0, 1));
    };
    onPageScroll(read, this.abort.signal);
    const rect = this.getBoundingClientRect();
    this.glide.to(clamp(-rect.top / Math.max(1, rect.height - window.innerHeight), 0, 1), true);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.glide?.stop();
  }

  paint(progress) {
    this.style.setProperty('--aa-p', progress.toFixed(4));
    const steps = this.count - 1;
    // A short rest on the first and on the last, so each is seen settled.
    const position = clamp((progress - 0.05) / 0.9, 0, 1) * steps;
    const lower = Math.min(steps - 1, Math.floor(position));
    // The one beneath stays whole while the next arrives over it: no dip to black between them.
    const arriving = smooth(clamp((position - lower - 0.14) / 0.72, 0, 1));
    for (const group of this.groups) {
      group.forEach((element, index) => {
        element.style.opacity = index === lower ? '1' : index === lower + 1 ? arriving.toFixed(3) : '0';
      });
    }
    for (const list of this.names) {
      list.forEach((item, index) => {
        item.style.opacity = smooth(clamp(1 - Math.abs(position - index) / 0.42, 0, 1)).toFixed(3);
      });
    }
    // The moon drifts very slightly as the page moves: closer, and a little higher.
    if (this.moon) this.moon.style.transform = `translate3d(0, ${((0.5 - progress) * 2.4).toFixed(3)}%, 0) scale(${(1 + progress * 0.05).toFixed(4)})`;
  }
}

// Scrolling is left entirely to the browser. An earlier version eased mouse-wheel steps in
// script; it fought the base theme's own `scroll-behavior: smooth` and the page barely moved
// (measured: 8 wheel notches moved 52 px instead of 800). Do not add wheel handling back
// without running tools/measure_scroll.mjs with WHEEL=1.

if (!customElements.get('aa-scroll-stage')) customElements.define('aa-scroll-stage', AaScrollStage);
if (!customElements.get('aa-garden')) customElements.define('aa-garden', AaGarden);
settlePostedForms();
if (!customElements.get('aa-scroll-steps')) customElements.define('aa-scroll-steps', AaScrollSteps);
if (!customElements.get('aa-door-stage')) customElements.define('aa-door-stage', AaDoorStage);
if (!customElements.get('aa-waitlist')) customElements.define('aa-waitlist', AaWaitlist);
if (!customElements.get('aa-lunar-turn')) customElements.define('aa-lunar-turn', AaLunarTurn);

initReveals(document);
// The theme editor re-renders sections in place.
document.addEventListener('shopify:section:load', (event) => initReveals(event.target));
